import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
    X, Save, Pin, Lock, Users, Globe, Bold, Italic, Code, List, ListOrdered,
    Link2, Hash, CheckSquare, Loader2, Paperclip, ChevronDown,
    AlertTriangle, Type, Heading1, Heading2, Heading3, Plus, Smile,
    Maximize2, Minimize2, Briefcase
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { uploadNoteAttachment } from '../api/notes';
import CasePicker from './CasePicker';

// Emoji categories for the picker
const EMOJI_CATEGORIES = [
    {
        name: 'Smileys',
        emojis: ['😀', '😃', '😄', '😁', '😅', '😂', '🤣', '😊', '😇', '🙂', '😉', '😌', '😍', '🥰', '😘', '😋', '😎', '🤩', '🥳', '😏', '🤔', '🤗', '😬', '😮', '🙄', '😴']
    },
    {
        name: 'Gestures',
        emojis: ['👍', '👎', '👌', '✌️', '🤞', '🤝', '👏', '🙌', '💪', '🤜', '🤛', '☝️', '👆', '👇', '👈', '👉', '🖐️', '✋', '👋', '🤚', '✍️', '🙏']
    },
    {
        name: 'Business',
        emojis: ['💼', '📊', '📈', '📉', '💰', '💵', '💳', '🏦', '📋', '📝', '✅', '❌', '⚠️', '🔔', '📅', '⏰', '🎯', '🏆', '⭐', '💡', '🔑', '📌', '📎', '🗂️', '📁', '📂']
    },
    {
        name: 'Status',
        emojis: ['✅', '❌', '⏳', '⌛', '🔄', '🚀', '🎉', '🔥', '💯', '⚡', '❗', '❓', '💬', '📣', '🔒', '🔓', '🛑', '✨', '🌟', '💎', '🏁', '🚧']
    },
    {
        name: 'Objects',
        emojis: ['📱', '💻', '🖥️', '📧', '📞', '🏠', '🏢', '🚗', '✈️', '📦', '🎁', '🔧', '🔨', '📐', '📏', '🗑️', '📮', '🗃️', '🗄️', '📚', '📖', '🔍']
    },
    {
        name: 'Hearts',
        emojis: ['❤️', '🧡', '💛', '💚', '💙', '💜', '🖤', '🤍', '🤎', '💔', '❤️‍🔥', '💕', '💞', '💓', '💗', '💖', '💘', '💝']
    }
];

const VISIBILITY_OPTIONS = [
    { value: 'PRIVATE', label: 'Private', icon: Lock, description: 'Only you can see', color: 'slate' },
    { value: 'PROTECTED', label: 'Team', icon: Users, description: 'Team members can see', color: 'sky' },
    { value: 'PUBLIC', label: 'Public', icon: Globe, description: 'Anyone can see', color: 'emerald' }
];

// Parse a line to determine its type and content
const parseLine = (line) => {
    // Checked task
    if (line.match(/^-\s*\[x\]\s*/i)) {
        return { type: 'task', checked: true, content: line.replace(/^-\s*\[x\]\s*/i, '') };
    }
    // Unchecked task
    if (line.match(/^-\s*\[\s*\]\s*/)) {
        return { type: 'task', checked: false, content: line.replace(/^-\s*\[\s*\]\s*/, '') };
    }
    // Headers
    if (line.startsWith('### ')) {
        return { type: 'h3', content: line.slice(4) };
    }
    if (line.startsWith('## ')) {
        return { type: 'h2', content: line.slice(3) };
    }
    if (line.startsWith('# ')) {
        return { type: 'h1', content: line.slice(2) };
    }
    // Bullet list
    if (line.startsWith('- ')) {
        return { type: 'bullet', content: line.slice(2) };
    }
    // Numbered list
    const numberedMatch = line.match(/^(\d+)\.\s+(.*)$/);
    if (numberedMatch) {
        return { type: 'numbered', number: numberedMatch[1], content: numberedMatch[2] };
    }
    // Tag line (starts with #)
    if (line.startsWith('#') && !line.startsWith('# ')) {
        return { type: 'tag', content: line };
    }
    // Empty line
    if (line.trim() === '') {
        return { type: 'empty', content: '' };
    }
    // Regular paragraph
    return { type: 'paragraph', content: line };
};

// Render inline formatting (bold, italic, code, links, tags)
const RenderInline = ({ text }) => {
    if (!text) return null;

    const parts = [];
    let remaining = text;
    let key = 0;

    while (remaining.length > 0) {
        // Bold **text**
        const boldMatch = remaining.match(/^\*\*(.+?)\*\*/);
        if (boldMatch) {
            parts.push(<strong key={key++} className="font-semibold">{boldMatch[1]}</strong>);
            remaining = remaining.slice(boldMatch[0].length);
            continue;
        }

        // Italic *text*
        const italicMatch = remaining.match(/^\*([^*]+?)\*/);
        if (italicMatch) {
            parts.push(<em key={key++} className="italic">{italicMatch[1]}</em>);
            remaining = remaining.slice(italicMatch[0].length);
            continue;
        }

        // Code `text`
        const codeMatch = remaining.match(/^`([^`]+)`/);
        if (codeMatch) {
            parts.push(
                <code key={key++} className="px-1.5 py-0.5 dark:bg-slate-700/50 bg-slate-100 rounded text-amber-600 dark:text-amber-400 text-[0.9em] font-mono">
                    {codeMatch[1]}
                </code>
            );
            remaining = remaining.slice(codeMatch[0].length);
            continue;
        }

        // Tags #tag
        const tagMatch = remaining.match(/^#([\w-]+)/);
        if (tagMatch) {
            parts.push(
                <span key={key++} className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/20">
                    #{tagMatch[1]}
                </span>
            );
            remaining = remaining.slice(tagMatch[0].length);
            continue;
        }

        // Links [text](url)
        const linkMatch = remaining.match(/^\[([^\]]+)\]\(([^)]+)\)/);
        if (linkMatch) {
            parts.push(
                <a
                    key={key++}
                    href={linkMatch[2]}
                    className="text-sky-500 hover:text-sky-400 underline decoration-sky-500/30 hover:decoration-sky-400 transition"
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    {linkMatch[1]}
                </a>
            );
            remaining = remaining.slice(linkMatch[0].length);
            continue;
        }

        // Regular character
        parts.push(remaining[0]);
        remaining = remaining.slice(1);
    }

    return <>{parts}</>;
};

// Individual editable block component
const EditableBlock = ({
    line,
    index,
    isActive,
    onFocus,
    onChange,
    onKeyDown,
    onToggleTask,
    blockRef
}) => {
    const parsed = parseLine(line);
    const inputRef = useRef(null);

    useEffect(() => {
        if (isActive && inputRef.current) {
            inputRef.current.focus();
        }
    }, [isActive]);

    const handleInput = (e) => {
        let newValue = e.target.value;

        // Preserve the line prefix based on type
        if (parsed.type === 'task') {
            const prefix = parsed.checked ? '- [x] ' : '- [ ] ';
            onChange(index, prefix + newValue);
        } else if (parsed.type === 'h1') {
            onChange(index, '# ' + newValue);
        } else if (parsed.type === 'h2') {
            onChange(index, '## ' + newValue);
        } else if (parsed.type === 'h3') {
            onChange(index, '### ' + newValue);
        } else if (parsed.type === 'bullet') {
            onChange(index, '- ' + newValue);
        } else if (parsed.type === 'numbered') {
            onChange(index, parsed.number + '. ' + newValue);
        } else {
            onChange(index, newValue);
        }
    };

    const commonInputClass = "w-full bg-transparent border-none outline-none resize-none";

    // Task (checkbox) block
    if (parsed.type === 'task') {
        return (
            <div
                ref={blockRef}
                className={`group flex items-start gap-3 py-1.5 px-2 -mx-2 rounded-lg transition-colors ${isActive ? 'bg-slate-100/50 dark:bg-slate-800/30' : 'hover:bg-slate-50 dark:hover:bg-slate-800/20'}`}
                onClick={() => onFocus(index)}
            >
                <button
                    onClick={(e) => {
                        e.stopPropagation();
                        onToggleTask(index);
                    }}
                    className={`mt-0.5 w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${
                        parsed.checked
                            ? 'bg-emerald-500 border-emerald-500 text-white'
                            : 'border-slate-300 dark:border-slate-600 hover:border-emerald-400 dark:hover:border-emerald-500'
                    }`}
                >
                    {parsed.checked && (
                        <svg className="w-3 h-3" viewBox="0 0 12 12" fill="none">
                            <path d="M2 6L5 9L10 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                    )}
                </button>
                {isActive ? (
                    <input
                        ref={inputRef}
                        type="text"
                        value={parsed.content}
                        onChange={handleInput}
                        onKeyDown={(e) => onKeyDown(e, index)}
                        className={`${commonInputClass} flex-1 dark:text-slate-200 text-slate-700 ${parsed.checked ? 'line-through opacity-60' : ''}`}
                        placeholder="Task description..."
                    />
                ) : (
                    <span className={`flex-1 dark:text-slate-200 text-slate-700 ${parsed.checked ? 'line-through opacity-60' : ''}`}>
                        {parsed.content ? <RenderInline text={parsed.content} /> : <span className="text-slate-400">Task description...</span>}
                    </span>
                )}
            </div>
        );
    }

    // Heading blocks
    if (parsed.type === 'h1') {
        return (
            <div
                ref={blockRef}
                className={`py-2 px-2 -mx-2 rounded-lg transition-colors ${isActive ? 'bg-slate-100/50 dark:bg-slate-800/30' : 'hover:bg-slate-50 dark:hover:bg-slate-800/20'}`}
                onClick={() => onFocus(index)}
            >
                {isActive ? (
                    <input
                        ref={inputRef}
                        type="text"
                        value={parsed.content}
                        onChange={handleInput}
                        onKeyDown={(e) => onKeyDown(e, index)}
                        className={`${commonInputClass} text-2xl font-bold dark:text-white text-slate-900`}
                        placeholder="Heading 1"
                    />
                ) : (
                    <h1 className="text-2xl font-bold dark:text-white text-slate-900">
                        {parsed.content || <span className="text-slate-400">Heading 1</span>}
                    </h1>
                )}
            </div>
        );
    }

    if (parsed.type === 'h2') {
        return (
            <div
                ref={blockRef}
                className={`py-1.5 px-2 -mx-2 rounded-lg transition-colors ${isActive ? 'bg-slate-100/50 dark:bg-slate-800/30' : 'hover:bg-slate-50 dark:hover:bg-slate-800/20'}`}
                onClick={() => onFocus(index)}
            >
                {isActive ? (
                    <input
                        ref={inputRef}
                        type="text"
                        value={parsed.content}
                        onChange={handleInput}
                        onKeyDown={(e) => onKeyDown(e, index)}
                        className={`${commonInputClass} text-xl font-semibold dark:text-white text-slate-900`}
                        placeholder="Heading 2"
                    />
                ) : (
                    <h2 className="text-xl font-semibold dark:text-white text-slate-900">
                        {parsed.content || <span className="text-slate-400">Heading 2</span>}
                    </h2>
                )}
            </div>
        );
    }

    if (parsed.type === 'h3') {
        return (
            <div
                ref={blockRef}
                className={`py-1 px-2 -mx-2 rounded-lg transition-colors ${isActive ? 'bg-slate-100/50 dark:bg-slate-800/30' : 'hover:bg-slate-50 dark:hover:bg-slate-800/20'}`}
                onClick={() => onFocus(index)}
            >
                {isActive ? (
                    <input
                        ref={inputRef}
                        type="text"
                        value={parsed.content}
                        onChange={handleInput}
                        onKeyDown={(e) => onKeyDown(e, index)}
                        className={`${commonInputClass} text-lg font-semibold dark:text-slate-100 text-slate-800`}
                        placeholder="Heading 3"
                    />
                ) : (
                    <h3 className="text-lg font-semibold dark:text-slate-100 text-slate-800">
                        {parsed.content || <span className="text-slate-400">Heading 3</span>}
                    </h3>
                )}
            </div>
        );
    }

    // Bullet list
    if (parsed.type === 'bullet') {
        return (
            <div
                ref={blockRef}
                className={`flex items-start gap-3 py-1 px-2 -mx-2 rounded-lg transition-colors ${isActive ? 'bg-slate-100/50 dark:bg-slate-800/30' : 'hover:bg-slate-50 dark:hover:bg-slate-800/20'}`}
                onClick={() => onFocus(index)}
            >
                <span className="mt-2 w-1.5 h-1.5 rounded-full bg-slate-400 dark:bg-slate-500 flex-shrink-0" />
                {isActive ? (
                    <input
                        ref={inputRef}
                        type="text"
                        value={parsed.content}
                        onChange={handleInput}
                        onKeyDown={(e) => onKeyDown(e, index)}
                        className={`${commonInputClass} flex-1 dark:text-slate-200 text-slate-700`}
                        placeholder="List item"
                    />
                ) : (
                    <span className="flex-1 dark:text-slate-200 text-slate-700">
                        {parsed.content ? <RenderInline text={parsed.content} /> : <span className="text-slate-400">List item</span>}
                    </span>
                )}
            </div>
        );
    }

    // Numbered list
    if (parsed.type === 'numbered') {
        return (
            <div
                ref={blockRef}
                className={`flex items-start gap-3 py-1 px-2 -mx-2 rounded-lg transition-colors ${isActive ? 'bg-slate-100/50 dark:bg-slate-800/30' : 'hover:bg-slate-50 dark:hover:bg-slate-800/20'}`}
                onClick={() => onFocus(index)}
            >
                <span className="text-slate-400 dark:text-slate-500 font-medium min-w-[1.5rem]">{parsed.number}.</span>
                {isActive ? (
                    <input
                        ref={inputRef}
                        type="text"
                        value={parsed.content}
                        onChange={handleInput}
                        onKeyDown={(e) => onKeyDown(e, index)}
                        className={`${commonInputClass} flex-1 dark:text-slate-200 text-slate-700`}
                        placeholder="List item"
                    />
                ) : (
                    <span className="flex-1 dark:text-slate-200 text-slate-700">
                        {parsed.content ? <RenderInline text={parsed.content} /> : <span className="text-slate-400">List item</span>}
                    </span>
                )}
            </div>
        );
    }

    // Tag line
    if (parsed.type === 'tag') {
        return (
            <div
                ref={blockRef}
                className={`py-1.5 px-2 -mx-2 rounded-lg transition-colors ${isActive ? 'bg-slate-100/50 dark:bg-slate-800/30' : 'hover:bg-slate-50 dark:hover:bg-slate-800/20'}`}
                onClick={() => onFocus(index)}
            >
                {isActive ? (
                    <input
                        ref={inputRef}
                        type="text"
                        value={parsed.content}
                        onChange={(e) => onChange(index, e.target.value)}
                        onKeyDown={(e) => onKeyDown(e, index)}
                        className={`${commonInputClass} dark:text-slate-200 text-slate-700`}
                        placeholder="#tag"
                    />
                ) : (
                    <div className="flex flex-wrap gap-2">
                        <RenderInline text={parsed.content} />
                    </div>
                )}
            </div>
        );
    }

    // Empty line
    if (parsed.type === 'empty') {
        return (
            <div
                ref={blockRef}
                className={`py-1 px-2 -mx-2 rounded-lg transition-colors min-h-[28px] ${isActive ? 'bg-slate-100/50 dark:bg-slate-800/30' : 'hover:bg-slate-50 dark:hover:bg-slate-800/20'}`}
                onClick={() => onFocus(index)}
            >
                {isActive && (
                    <input
                        ref={inputRef}
                        type="text"
                        value=""
                        onChange={(e) => onChange(index, e.target.value)}
                        onKeyDown={(e) => onKeyDown(e, index)}
                        className={`${commonInputClass} dark:text-slate-200 text-slate-700`}
                        placeholder="Type '/' for commands, or start writing..."
                    />
                )}
            </div>
        );
    }

    // Default paragraph
    return (
        <div
            ref={blockRef}
            className={`py-1 px-2 -mx-2 rounded-lg transition-colors ${isActive ? 'bg-slate-100/50 dark:bg-slate-800/30' : 'hover:bg-slate-50 dark:hover:bg-slate-800/20'}`}
            onClick={() => onFocus(index)}
        >
            {isActive ? (
                <input
                    ref={inputRef}
                    type="text"
                    value={parsed.content}
                    onChange={(e) => onChange(index, e.target.value)}
                    onKeyDown={(e) => onKeyDown(e, index)}
                    className={`${commonInputClass} dark:text-slate-200 text-slate-700 leading-relaxed`}
                    placeholder="Type something..."
                />
            ) : (
                <p className="dark:text-slate-200 text-slate-700 leading-relaxed">
                    {parsed.content ? <RenderInline text={parsed.content} /> : <span className="text-slate-400">Type something...</span>}
                </p>
            )}
        </div>
    );
};

// Block type menu (appears when typing /)
const BlockTypeMenu = ({ position, onSelect, onClose }) => {
    const options = [
        { type: 'paragraph', label: 'Text', icon: Type, description: 'Plain text', prefix: '' },
        { type: 'h1', label: 'Heading 1', icon: Heading1, description: 'Large heading', prefix: '# ' },
        { type: 'h2', label: 'Heading 2', icon: Heading2, description: 'Medium heading', prefix: '## ' },
        { type: 'h3', label: 'Heading 3', icon: Heading3, description: 'Small heading', prefix: '### ' },
        { type: 'bullet', label: 'Bullet List', icon: List, description: 'Simple list', prefix: '- ' },
        { type: 'numbered', label: 'Numbered List', icon: ListOrdered, description: 'Numbered list', prefix: '1. ' },
        { type: 'task', label: 'To-do', icon: CheckSquare, description: 'Task with checkbox', prefix: '- [ ] ' },
    ];

    return (
        <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="absolute z-50 w-64 dark:bg-slate-800 bg-white border dark:border-slate-700 border-slate-200 rounded-xl shadow-xl overflow-hidden"
            style={{ left: position.x, top: position.y }}
        >
            <div className="p-2">
                <p className="text-xs font-medium text-slate-400 dark:text-slate-500 px-2 py-1">BLOCKS</p>
                {options.map((opt) => (
                    <button
                        key={opt.type}
                        onClick={() => onSelect(opt.prefix)}
                        className="w-full flex items-center gap-3 px-2 py-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700/50 transition text-left"
                    >
                        <div className="w-10 h-10 rounded-lg bg-slate-100 dark:bg-slate-700 flex items-center justify-center">
                            <opt.icon size={20} className="text-slate-500 dark:text-slate-400" />
                        </div>
                        <div>
                            <div className="font-medium dark:text-white text-slate-900 text-sm">{opt.label}</div>
                            <div className="text-xs text-slate-400 dark:text-slate-500">{opt.description}</div>
                        </div>
                    </button>
                ))}
            </div>
        </motion.div>
    );
};

// Emoji Picker component
const EmojiPicker = ({ onSelect, onClose }) => {
    const [activeCategory, setActiveCategory] = useState(0);
    const [searchTerm, setSearchTerm] = useState('');
    const pickerRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (pickerRef.current && !pickerRef.current.contains(e.target)) {
                onClose();
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [onClose]);

    const filteredEmojis = searchTerm
        ? EMOJI_CATEGORIES.flatMap(cat => cat.emojis).filter(() => true) // Show all when searching
        : EMOJI_CATEGORIES[activeCategory].emojis;

    return (
        <motion.div
            ref={pickerRef}
            initial={{ opacity: 0, scale: 0.95, y: -10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: -10 }}
            className="absolute top-full left-0 mt-2 w-80 dark:bg-slate-800 bg-white border dark:border-slate-700 border-slate-200 rounded-xl shadow-2xl overflow-hidden z-50"
        >
            {/* Search */}
            <div className="p-2 border-b dark:border-slate-700 border-slate-200">
                <input
                    type="text"
                    placeholder="Search emojis..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full px-3 py-2 text-sm dark:bg-slate-700 bg-slate-100 border-none rounded-lg dark:text-white text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
                />
            </div>

            {/* Category tabs */}
            {!searchTerm && (
                <div className="flex gap-1 p-2 border-b dark:border-slate-700 border-slate-200 overflow-x-auto">
                    {EMOJI_CATEGORIES.map((cat, idx) => (
                        <button
                            key={cat.name}
                            onClick={() => setActiveCategory(idx)}
                            className={`px-3 py-1.5 text-xs font-medium rounded-lg whitespace-nowrap transition ${
                                activeCategory === idx
                                    ? 'bg-amber-500/20 text-amber-500'
                                    : 'dark:text-slate-400 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700'
                            }`}
                        >
                            {cat.name}
                        </button>
                    ))}
                </div>
            )}

            {/* Emoji grid */}
            <div className="p-2 max-h-48 overflow-y-auto">
                <div className="grid grid-cols-8 gap-1">
                    {filteredEmojis.map((emoji, idx) => (
                        <button
                            key={idx}
                            onClick={() => {
                                onSelect(emoji);
                                onClose();
                            }}
                            className="w-8 h-8 flex items-center justify-center text-xl hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition"
                        >
                            {emoji}
                        </button>
                    ))}
                </div>
            </div>

            {/* Quick access - recently used could go here */}
            <div className="p-2 border-t dark:border-slate-700 border-slate-200 bg-slate-50 dark:bg-slate-900/50">
                <p className="text-xs text-slate-400 dark:text-slate-500 text-center">
                    Click an emoji to insert
                </p>
            </div>
        </motion.div>
    );
};

const AUTOSAVE_DELAY = 1500; // 1.5 seconds after user stops typing

const NoteEditor = ({ note, onSave, onAutosave, onClose, isSaving, cases = [], onNavigateToCase }) => {
    const [lines, setLines] = useState(() => {
        const content = note?.content || '';
        return content ? content.split('\n') : [''];
    });
    const [activeLineIndex, setActiveLineIndex] = useState(0);
    const [visibility, setVisibility] = useState(note?.visibility || 'PRIVATE');
    const [pinned, setPinned] = useState(note?.pinned || false);
    const [attachments, setAttachments] = useState(note?.attachments || []);
    const [isUploading, setIsUploading] = useState(false);
    const [showVisibilityDropdown, setShowVisibilityDropdown] = useState(false);
    const [showEmojiPicker, setShowEmojiPicker] = useState(false);
    const [error, setError] = useState(null);
    const [showBlockMenu, setShowBlockMenu] = useState(false);
    const [blockMenuPosition, setBlockMenuPosition] = useState({ x: 0, y: 0 });
    const [isExpanded, setIsExpanded] = useState(false);
    const [isCasePickerOpen, setIsCasePickerOpen] = useState(false);
    const [linkedCase, setLinkedCase] = useState(null);

    // Autosave state
    const [saveStatus, setSaveStatus] = useState('saved'); // 'saved', 'saving', 'unsaved', 'error'
    const [lastSavedContent, setLastSavedContent] = useState(note?.content || '');
    const [currentNoteId, setCurrentNoteId] = useState(note?.id || null);
    const autosaveTimerRef = useRef(null);

    const fileInputRef = useRef(null);
    const editorRef = useRef(null);
    const blockRefs = useRef([]);

    const content = lines.join('\n');

    // Effect to initialize linked case from note prop
    useEffect(() => {
        const caseId = note?.caseId || note?.case_id;
        if (caseId && cases.length > 0) {
            const foundCase = cases.find(c => String(c.id) === String(caseId));
            if (foundCase) {
                setLinkedCase({ id: foundCase.id, name: foundCase.name || `Case #${foundCase.id}` });
            }
        }
    }, [note, cases]);

    // Autosave effect - triggers after user stops typing
    useEffect(() => {
        // Skip if no autosave handler provided
        if (!onAutosave) return;

        // Skip autosave if content hasn't changed from last saved
        if (content === lastSavedContent) {
            setSaveStatus('saved');
            return;
        }

        // Skip if content is empty (don't autosave empty notes)
        if (!content.trim()) {
            return;
        }

        // Mark as unsaved
        setSaveStatus('unsaved');

        // Clear existing timer
        if (autosaveTimerRef.current) {
            clearTimeout(autosaveTimerRef.current);
        }

        // Set new autosave timer
        autosaveTimerRef.current = setTimeout(async () => {
            setSaveStatus('saving');
            setError(null);

            try {
                const noteData = {
                    content: content.trim(),
                    visibility,
                    pinned,
                    attachments: attachments.map(a => a.id),
                    caseId: linkedCase?.id || null,
                };

                // Call parent's onAutosave which handles create vs update without closing
                await onAutosave(noteData);

                setLastSavedContent(content);
                setSaveStatus('saved');
            } catch (err) {
                console.error('Autosave failed:', err);
                setSaveStatus('error');
                setError('Autosave failed. Your changes may not be saved.');
            }
        }, AUTOSAVE_DELAY);

        // Cleanup timer on unmount or when dependencies change
        return () => {
            if (autosaveTimerRef.current) {
                clearTimeout(autosaveTimerRef.current);
            }
        };
    }, [content, visibility, pinned, attachments, lastSavedContent, onAutosave]);

    // Cleanup autosave timer on unmount
    useEffect(() => {
        return () => {
            if (autosaveTimerRef.current) {
                clearTimeout(autosaveTimerRef.current);
            }
        };
    }, []);

    // Save immediately when closing if there are unsaved changes
    const handleClose = useCallback(async () => {
        // Clear any pending autosave timer
        if (autosaveTimerRef.current) {
            clearTimeout(autosaveTimerRef.current);
        }

        if (saveStatus === 'unsaved' && content.trim() && onAutosave) {
            setSaveStatus('saving');
            try {
                await onAutosave({
                    content: content.trim(),
                    visibility,
                    pinned,
                    attachments: attachments.map(a => a.id)
                });
                setSaveStatus('saved');
            } catch (err) {
                console.error('Save on close failed:', err);
            }
        }
        onClose();
    }, [saveStatus, content, visibility, pinned, attachments, onAutosave, onClose]);

    const handleLineChange = useCallback((index, newValue) => {
        // Check for slash command
        if (newValue === '/') {
            const blockEl = blockRefs.current[index];
            if (blockEl) {
                const rect = blockEl.getBoundingClientRect();
                const editorRect = editorRef.current?.getBoundingClientRect();
                if (editorRect) {
                    setBlockMenuPosition({
                        x: rect.left - editorRect.left,
                        y: rect.bottom - editorRect.top + 8
                    });
                    setShowBlockMenu(true);
                }
            }
        } else {
            setShowBlockMenu(false);
        }

        setLines(prev => {
            const newLines = [...prev];
            newLines[index] = newValue;
            return newLines;
        });
    }, []);

    const handleKeyDown = useCallback((e, index) => {
        // Enter to create new line
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            const currentLine = lines[index];
            const parsed = parseLine(currentLine);

            // If current line is a task/bullet/numbered list, continue the format
            let newLinePrefix = '';
            if (parsed.type === 'task' && parsed.content.trim() !== '') {
                newLinePrefix = '- [ ] ';
            } else if (parsed.type === 'bullet' && parsed.content.trim() !== '') {
                newLinePrefix = '- ';
            } else if (parsed.type === 'numbered' && parsed.content.trim() !== '') {
                newLinePrefix = (parseInt(parsed.number) + 1) + '. ';
            }

            // If the line is empty (just the prefix), remove the prefix instead
            if ((parsed.type === 'task' || parsed.type === 'bullet' || parsed.type === 'numbered') && parsed.content.trim() === '') {
                setLines(prev => {
                    const newLines = [...prev];
                    newLines[index] = '';
                    return newLines;
                });
                return;
            }

            setLines(prev => {
                const newLines = [...prev];
                newLines.splice(index + 1, 0, newLinePrefix);
                return newLines;
            });
            setActiveLineIndex(index + 1);
        }

        // Backspace at start of line to merge with previous
        if (e.key === 'Backspace' && e.target.selectionStart === 0 && index > 0) {
            e.preventDefault();
            const currentLine = lines[index];
            setLines(prev => {
                const newLines = [...prev];
                newLines[index - 1] = newLines[index - 1] + currentLine;
                newLines.splice(index, 1);
                return newLines;
            });
            setActiveLineIndex(index - 1);
        }

        // Arrow up/down navigation
        if (e.key === 'ArrowUp' && index > 0) {
            e.preventDefault();
            setActiveLineIndex(index - 1);
        }
        if (e.key === 'ArrowDown' && index < lines.length - 1) {
            e.preventDefault();
            setActiveLineIndex(index + 1);
        }

        // Tab for indentation (future feature)
        if (e.key === 'Tab') {
            e.preventDefault();
        }

        // Escape to close block menu, then collapse expanded, then close
        if (e.key === 'Escape') {
            if (showBlockMenu) {
                setShowBlockMenu(false);
            } else if (isExpanded) {
                setIsExpanded(false);
            } else {
                onClose();
            }
        }
    }, [lines, showBlockMenu, isExpanded, onClose]);

    const handleToggleTask = useCallback((index) => {
        setLines(prev => {
            const newLines = [...prev];
            const line = newLines[index];
            if (line.match(/^-\s*\[x\]\s*/i)) {
                newLines[index] = line.replace(/^-\s*\[x\]\s*/i, '- [ ] ');
            } else if (line.match(/^-\s*\[\s*\]\s*/)) {
                newLines[index] = line.replace(/^-\s*\[\s*\]\s*/, '- [x] ');
            }
            return newLines;
        });
    }, []);

    const handleBlockTypeSelect = useCallback((prefix) => {
        setLines(prev => {
            const newLines = [...prev];
            newLines[activeLineIndex] = prefix;
            return newLines;
        });
        setShowBlockMenu(false);
    }, [activeLineIndex]);

    const insertFormatting = useCallback((prefix, suffix = '') => {
        setLines(prev => {
            const newLines = [...prev];
            const currentLine = newLines[activeLineIndex] || '';
            newLines[activeLineIndex] = currentLine + prefix + suffix;
            return newLines;
        });
    }, [activeLineIndex]);

    const insertEmoji = useCallback((emoji) => {
        setLines(prev => {
            const newLines = [...prev];
            const currentLine = newLines[activeLineIndex] || '';
            newLines[activeLineIndex] = currentLine + emoji;
            return newLines;
        });
        setShowEmojiPicker(false);
    }, [activeLineIndex]);

    const handleSave = async () => {
        if (!content.trim()) {
            setError('Note content cannot be empty');
            return;
        }

        setError(null);
        try {
            await onSave({
                content: content.trim(),
                visibility,
                pinned,
                attachments: attachments.map(a => a.id),
                caseId: linkedCase?.id || null,
            });
        } catch (err) {
            setError(err.message || 'Failed to save note');
        }
    };

    const handleCaseSelect = (caseData) => {
        setLinkedCase(caseData);
        setIsCasePickerOpen(false);
    };

    const handleFileUpload = async (e) => {
        const files = Array.from(e.target.files || []);
        if (files.length === 0) return;

        setIsUploading(true);
        try {
            const uploadPromises = files.map(file => uploadNoteAttachment(file));
            const results = await Promise.all(uploadPromises);
            const newAttachments = results.map(r => r.data);
            setAttachments(prev => [...prev, ...newAttachments]);
        } catch (err) {
            console.error('Upload failed:', err);
            setError('Failed to upload file(s)');
        } finally {
            setIsUploading(false);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    const removeAttachment = (attachmentId) => {
        setAttachments(prev => prev.filter(a => a.id !== attachmentId));
    };

    const selectedVisibility = VISIBILITY_OPTIONS.find(v => v.value === visibility);

    // Quick format buttons
    const toolbarItems = [
        { icon: Bold, action: () => insertFormatting('**', '**'), title: 'Bold' },
        { icon: Italic, action: () => insertFormatting('*', '*'), title: 'Italic' },
        { icon: Code, action: () => insertFormatting('`', '`'), title: 'Code' },
        { type: 'divider' },
        { icon: List, action: () => handleBlockTypeSelect('- '), title: 'Bullet list' },
        { icon: ListOrdered, action: () => handleBlockTypeSelect('1. '), title: 'Numbered list' },
        { icon: CheckSquare, action: () => handleBlockTypeSelect('- [ ] '), title: 'To-do' },
        { type: 'divider' },
        { icon: Link2, action: () => insertFormatting('[', '](url)'), title: 'Link' },
        { icon: Hash, action: () => insertFormatting('#'), title: 'Tag' },
        { icon: Smile, action: () => setShowEmojiPicker(!showEmojiPicker), title: 'Emoji', isEmojiButton: true },
        { type: 'divider' },
        { icon: Paperclip, action: () => fileInputRef.current?.click(), title: 'Attach file', loading: isUploading },
    ];

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className={`fixed inset-0 z-[150] flex items-center justify-center dark:bg-slate-950/90 bg-slate-500/50 backdrop-blur-sm ${isExpanded ? 'p-0' : 'p-4'}`}
            onClick={(e) => e.target === e.currentTarget && onClose()}
        >
            <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.95, opacity: 0 }}
                layout
                className={`dark:bg-slate-900 bg-white border dark:border-slate-700 border-slate-200 shadow-2xl flex flex-col overflow-hidden transition-all duration-300 ${
                    isExpanded
                        ? 'w-full h-full max-w-full max-h-full rounded-none'
                        : 'w-full max-w-3xl max-h-[90vh] rounded-2xl'
                }`}
            >
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b dark:border-slate-800 border-slate-200">
                    <h2 className="text-lg font-semibold dark:text-white text-slate-900">
                        {note ? 'Edit Note' : 'New Note'}
                    </h2>
                    <div className="flex items-center gap-1">
                        {/* Expand/Collapse Button */}
                        <button
                            onClick={() => setIsExpanded(!isExpanded)}
                            className="p-2 dark:text-slate-400 text-slate-500 hover:text-slate-900 dark:hover:text-white rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition"
                            title={isExpanded ? 'Exit full screen' : 'Full screen'}
                        >
                            {isExpanded ? <Minimize2 size={18} /> : <Maximize2 size={18} />}
                        </button>
                        {/* Close Button */}
                        <button
                            onClick={onClose}
                            className="p-2 dark:text-slate-400 text-slate-500 hover:text-slate-900 dark:hover:text-white rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition"
                        >
                            <X size={20} />
                        </button>
                    </div>
                </div>

                {/* Toolbar */}
                <div className="flex items-center gap-0.5 px-4 py-2 border-b dark:border-slate-800 border-slate-200 overflow-x-auto">
                    {toolbarItems.map((item, i) => {
                        if (item.type === 'divider') {
                            return <div key={i} className="w-px h-5 dark:bg-slate-700 bg-slate-200 mx-2" />;
                        }
                        // Special handling for emoji button with picker
                        if (item.isEmojiButton) {
                            return (
                                <div key={i} className="relative">
                                    <button
                                        onClick={item.action}
                                        className={`p-2 rounded-lg transition ${
                                            showEmojiPicker
                                                ? 'bg-amber-500/20 text-amber-500'
                                                : 'dark:text-slate-400 text-slate-500 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800'
                                        }`}
                                        title={item.title}
                                    >
                                        <item.icon size={16} />
                                    </button>
                                    <AnimatePresence>
                                        {showEmojiPicker && (
                                            <EmojiPicker
                                                onSelect={insertEmoji}
                                                onClose={() => setShowEmojiPicker(false)}
                                            />
                                        )}
                                    </AnimatePresence>
                                </div>
                            );
                        }
                        return (
                            <button
                                key={i}
                                onClick={item.action}
                                disabled={item.loading}
                                className="p-2 dark:text-slate-400 text-slate-500 hover:text-slate-900 dark:hover:text-white rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition disabled:opacity-50"
                                title={item.title}
                            >
                                {item.loading ? <Loader2 size={16} className="animate-spin" /> : <item.icon size={16} />}
                            </button>
                        );
                    })}
                    <input
                        ref={fileInputRef}
                        type="file"
                        multiple
                        className="hidden"
                        onChange={handleFileUpload}
                        accept="image/*,.pdf,.doc,.docx,.txt,.xls,.xlsx"
                    />
                </div>

                {/* Content Area - Notion-like blocks */}
                <div
                    ref={editorRef}
                    className="flex-1 overflow-y-auto p-6 relative"
                    onClick={() => {
                        // Click on empty area focuses last line
                        if (lines.length > 0) {
                            setActiveLineIndex(lines.length - 1);
                        }
                    }}
                >
                    <div className="max-w-2xl mx-auto space-y-0.5" onClick={(e) => e.stopPropagation()}>
                        {lines.map((line, index) => (
                            <EditableBlock
                                key={index}
                                line={line}
                                index={index}
                                isActive={activeLineIndex === index}
                                onFocus={setActiveLineIndex}
                                onChange={handleLineChange}
                                onKeyDown={handleKeyDown}
                                onToggleTask={handleToggleTask}
                                blockRef={el => blockRefs.current[index] = el}
                            />
                        ))}
                        {/* Add new block button */}
                        <button
                            onClick={() => {
                                setLines(prev => [...prev, '']);
                                setActiveLineIndex(lines.length);
                            }}
                            className="flex items-center gap-2 py-2 px-2 -mx-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800/20 transition w-full text-left text-sm"
                        >
                            <Plus size={16} />
                            <span>Add a block</span>
                        </button>
                    </div>

                    {/* Block type menu */}
                    <AnimatePresence>
                        {showBlockMenu && (
                            <BlockTypeMenu
                                position={blockMenuPosition}
                                onSelect={handleBlockTypeSelect}
                                onClose={() => setShowBlockMenu(false)}
                            />
                        )}
                    </AnimatePresence>
                </div>

                {/* Attachments */}
                {attachments.length > 0 && (
                    <div className="px-4 py-2 border-t dark:border-slate-800 border-slate-200">
                        <div className="flex items-center gap-2 flex-wrap">
                            {attachments.map(att => (
                                <div
                                    key={att.id}
                                    className="flex items-center gap-2 px-3 py-1.5 dark:bg-slate-800 bg-slate-100 rounded-lg text-sm"
                                >
                                    <Paperclip size={14} className="dark:text-slate-400 text-slate-500" />
                                    <span className="dark:text-slate-300 text-slate-700 truncate max-w-[150px]">
                                        {att.filename || att.name || 'Attachment'}
                                    </span>
                                    <button
                                        onClick={() => removeAttachment(att.id)}
                                        className="text-slate-400 hover:text-red-500 transition"
                                    >
                                        <X size={14} />
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Error */}
                {error && (
                    <div className="px-4 py-2 bg-red-500/10 border-t border-red-500/20">
                        <div className="flex items-center gap-2 text-red-500 text-sm">
                            <AlertTriangle size={16} />
                            {error}
                        </div>
                    </div>
                )}

                {/* Footer */}
                <div className="flex items-center justify-between p-4 border-t dark:border-slate-800 border-slate-200 dark:bg-slate-900/50 bg-slate-50">
                    <div className="flex items-center gap-3">
                        {/* Pin Toggle */}
                        <button
                            onClick={() => setPinned(!pinned)}
                            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm transition ${pinned
                                ? 'bg-amber-500/20 text-amber-500'
                                : 'dark:bg-slate-800 bg-slate-200 dark:text-slate-400 text-slate-600 hover:text-amber-500'
                                }`}
                        >
                            <Pin size={14} className={pinned ? 'fill-current' : ''} />
                            {pinned ? 'Pinned' : 'Pin'}
                        </button>

                        {/* Link Case Button */}
                        {linkedCase ? (
                            <div className="flex items-center gap-1.5 pl-1 pr-1 py-1 rounded-lg text-sm bg-sky-500/10 text-sky-500">
                                <button
                                    onClick={() => onNavigateToCase?.(linkedCase.id)}
                                    className="flex items-center gap-1.5 pl-2 pr-1 py-0.5 rounded-md hover:bg-sky-500/20 transition"
                                >
                                    <Briefcase size={14} />
                                    <span className="font-medium truncate max-w-[120px]">{linkedCase.name}</span>
                                </button>
                                <button
                                    onClick={() => setLinkedCase(null)}
                                    className="p-0.5 rounded-full hover:bg-sky-500/20"
                                >
                                    <X size={14} />
                                </button>
                            </div>
                        ) : (
                            <button
                                onClick={() => setIsCasePickerOpen(true)}
                                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm dark:bg-slate-800 bg-slate-200 dark:text-slate-400 text-slate-600 hover:text-sky-500 transition"
                            >
                                <Briefcase size={14} />
                                Link case
                            </button>
                        )}


                        {/* Visibility Dropdown */}
                        <div className="relative">
                            <button
                                onClick={() => setShowVisibilityDropdown(!showVisibilityDropdown)}
                                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm dark:bg-slate-800 bg-slate-200 dark:text-slate-400 text-slate-600 hover:text-slate-900 dark:hover:text-white transition"
                            >
                                {selectedVisibility && <selectedVisibility.icon size={14} />}
                                {selectedVisibility?.label}
                                <ChevronDown size={14} />
                            </button>
                            {showVisibilityDropdown && (
                                <div className="absolute bottom-full left-0 mb-2 w-48 dark:bg-slate-800 bg-white border dark:border-slate-700 border-slate-200 rounded-lg shadow-xl overflow-hidden z-10">
                                    {VISIBILITY_OPTIONS.map(opt => (
                                        <button
                                            key={opt.value}
                                            onClick={() => {
                                                setVisibility(opt.value);
                                                setShowVisibilityDropdown(false);
                                            }}
                                            className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left transition ${visibility === opt.value
                                                ? 'dark:bg-slate-700 bg-slate-100'
                                                : 'hover:bg-slate-100 dark:hover:bg-slate-700'
                                                }`}
                                        >
                                            <opt.icon size={14} className={`text-${opt.color}-500`} />
                                            <div>
                                                <div className="dark:text-white text-slate-900 font-medium">{opt.label}</div>
                                                <div className="text-xs dark:text-slate-400 text-slate-500">{opt.description}</div>
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>

                    </div>

                    <div className="flex items-center gap-3">
                        {/* Autosave Status Indicator */}
                        <div className="flex items-center gap-2 text-xs">
                            {saveStatus === 'saving' && (
                                <span className="flex items-center gap-1.5 text-amber-500">
                                    <Loader2 size={14} className="animate-spin" />
                                    Saving...
                                </span>
                            )}
                            {saveStatus === 'saved' && (
                                <span className="flex items-center gap-1.5 text-emerald-500">
                                    <svg className="w-3.5 h-3.5" viewBox="0 0 12 12" fill="none">
                                        <path d="M2 6L5 9L10 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                    Saved
                                </span>
                            )}
                            {saveStatus === 'unsaved' && (
                                <span className="flex items-center gap-1.5 dark:text-slate-400 text-slate-500">
                                    <div className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
                                    Editing...
                                </span>
                            )}
                            {saveStatus === 'error' && (
                                <span className="flex items-center gap-1.5 text-red-500">
                                    <AlertTriangle size={14} />
                                    Save failed
                                </span>
                            )}
                        </div>

                        <span className="text-xs dark:text-slate-500 text-slate-400 hidden sm:inline">
                            Auto-saves as you type
                        </span>

                        <button
                            onClick={handleSave}
                            className="px-4 py-2 bg-amber-600 hover:bg-amber-500 text-white rounded-lg font-medium flex items-center gap-2 transition shadow-lg shadow-amber-900/20"
                        >
                            Done
                        </button>
                    </div>
                </div>
            </motion.div>

            <AnimatePresence>
                {isCasePickerOpen && (
                    <CasePicker
                        cases={cases}
                        onSelect={handleCaseSelect}
                        onClose={() => setIsCasePickerOpen(false)}
                    />
                )}
            </AnimatePresence>
        </motion.div>
    );
};

export default NoteEditor;
